import { Button, Csel, Input } from '@efira/ui';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { useAnalytics } from '../../api/queries';
/* Склонение - общим форматтером продукта, а не своей копией рядом: две
   копии расходятся на первом же слове (`lib/plural.ts`). */
import { minutes as minutesWord, runs as runsWord } from '../../lib/plural';
import type { AnalyticsPayload } from '../../api/queries';
import { useLiveKind } from '../liveState';

/**
 * Аналитика эфиров.
 *
 * СИМУЛЯТОР И ТЕСТОВЫЕ ДЕЙСТВИЯ В ЧИСЛА НЕ ПОПАДАЮТ. Это правило сервера, а
 * не подпись мелким шрифтом: событие помечено происхождением на входе, и в
 * счётчики эфира идут только реальные (`apps/server/src/live/session.ts`).
 * Час репетиции, посчитанный как эфир, - самый тихий способ испортить
 * данные, и заметить его по интерфейсу невозможно.
 *
 * ПОКАЗЫВАЕМ ТОЛЬКО ТО, ЧТО СЧИТАЕМ. Ноль - это утверждение «такого не
 * было», а не «мы не знаем»: числа, которого нет в наших данных, здесь нет
 * даже нулём. Поэтому вкладки короткие, а не заполнены рамками под будущие
 * графики.
 *
 * ПЕРИОД И ФИЛЬТРЫ ЖИВУТ В АДРЕСЕ. Ссылку на «подарки за 90 дней по одной
 * игре» можно отправить себе в заметки и открыть завтра - у эталона так же,
 * и это правильно: окно адресуемо целиком, а не только своим фактом.
 */
export const ANALYTICS_TABS = [
  { id: 'overview', title: 'Обзор' },
  { id: 'games', title: 'Игры' },
  { id: 'audience', title: 'Зрители' },
  { id: 'runs', title: 'Эфиры' },
] as const;

const RANGES = [
  { id: '7d', title: '7 дней' },
  { id: '30d', title: '30 дней' },
  { id: '90d', title: '90 дней' },
  { id: 'all', title: 'Всё время' },
  { id: 'custom', title: 'Свой период' },
] as const;

export function Analytics() {
  /* Место состояния эфира (единица 22): симулятор сюда не попадает. */
  const liveKind = useLiveKind();
  const { tab = 'overview' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const current = ANALYTICS_TABS.find((item) => item.id === tab) ?? ANALYTICS_TABS[0];
  const range = params.get('range') ?? '30d';
  const data = useAnalytics(params.toString());

  const set = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);

    if (value === null || value === '') {
      next.delete(key);
    } else {
      next.set(key, value);
    }

    setParams(next, { replace: true });
  };

  return (
    <div
      data-analytics="root"
      data-live-place="analytics"
      data-live-state={liveKind}
      className="flex min-h-[22rem] flex-col gap-3"
    >
      {/* Оговорка о симуляторе живёт ЗДЕСЬ И ТОЛЬКО ЗДЕСЬ: та же мысль в
          заглушке читалась повтором в пределах одного экрана. */}
      <p data-analytics="disclaimer" className="text-foreground/80 text-sm">
        Учитываются только эфиры в TikTok. Симулятор и тестовые действия не считаются.
      </p>

      {/* Вкладки: у каждой свой адрес, и открытая по ссылке вкладка
          открывается именно та, а не «первая». */}
      {/* Вкладки - ПОДЧЁРКИВАНИЕМ, а фильтр периода - сегментами: одинаковые
          плашки в двух соседних рядах читались как один орган, поставленный
          дважды. Линия под рядом отделяет шапку окна от содержимого. */}
      <div
        data-analytics="tabs"
        role="tablist"
        className="border-foreground/10 flex flex-wrap gap-4 border-b"
      >
        {ANALYTICS_TABS.map((item) => (
          <Button
            variant="ghost"
            key={item.id}
            role="tab"
            data-analytics-tab={item.id}
            aria-selected={item.id === current.id}
            className={`-mb-px border-b-2 px-1 pb-2 text-base ${
              item.id === current.id
                ? 'border-primary text-foreground font-semibold'
                : 'text-foreground/80 hover:text-foreground border-transparent'
            }`}
            onClick={() => {
              void navigate({
                pathname: `/app/analytics/${item.id}`,
                search: params.toString(),
              });
            }}
          >
            {item.title}
          </Button>
        ))}
      </div>

      <div
        data-analytics="filters"
        data-control-row="analytics-filters"
        className="@container flex flex-wrap items-end gap-2"
      >
        {/* Сегменты В ОБЩЕЙ ДОРОЖКЕ: пять отдельных чипов не читаются как
            один переключатель. Выбранный - нейтральной поверхностью:
            акцент в окне принадлежит активной ВКЛАДКЕ, и два одинаковых
            «выбрано» подряд не отвечают, что главнее. */}
        <div data-analytics="range" className="bg-muted/60 flex flex-wrap gap-1 rounded-lg p-1">
          {RANGES.map((item) => (
            <Button
              variant="ghost"
              key={item.id}
              data-analytics-range={item.id}
              aria-pressed={range === item.id}
              className={`rounded-md px-3 py-2 text-sm ${
                range === item.id
                  ? 'bg-background text-foreground font-medium shadow-sm'
                  : 'text-foreground/80 hover:text-foreground'
              }`}
              onClick={() => {
                set('range', item.id === '30d' ? null : item.id);
              }}
            >
              {item.title}
            </Button>
          ))}
        </div>

        {/* Без подписи сверху: значение само себя называет («Все игры»), а
            подпись добавляла третий кегль в шапку и ломала общую линию
            ряда. Имя для чтеца остаётся в `aria-label`. */}
        <Csel
          value={params.get('game') ?? ''}
          aria-label="Фильтр по игре"
          triggerClassName="w-44"
          options={[
            { value: '', label: 'Все игры' },
            ...(data.data?.filters.games ?? []).map((game) => ({
              value: game.id,
              label: game.title,
            })),
          ]}
          onChange={(value) => {
            set('game', value);
          }}
        />

        <Csel
          value={params.get('channel') ?? ''}
          aria-label="Фильтр по аккаунту TikTok"
          triggerClassName="w-44"
          options={[
            { value: '', label: 'Все аккаунты' },
            ...(data.data?.filters.channels ?? []).map((channel) => ({
              value: channel.id,
              label: `@${channel.handle}`,
            })),
          ]}
          onChange={(value) => {
            set('channel', value);
          }}
        />
      </div>

      {/* Свой период - два поля дат и сброс. Показывается только когда
          выбран: два пустых поля дат в обычном состоянии обещают
          настройку, которой человек не просил. */}
      {range === 'custom' && (
        <div data-analytics="custom" className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-xs">
            С
            <Input
              type="date"
              data-analytics="custom-from"
              className="w-40"
              value={params.get('from') ?? ''}
              onChange={(event) => {
                set('from', event.target.value);
              }}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs">
            По
            <Input
              type="date"
              data-analytics="custom-to"
              className="w-40"
              value={params.get('to') ?? ''}
              onChange={(event) => {
                set('to', event.target.value);
              }}
            />
          </label>

          <Button
            variant="secondary"
            size="s"

            data-analytics="custom-reset"
            /* Не разрушающее: сбрасывает фильтр дат, данные не трогает. */
            data-not-destructive="фильтр дат"
            onClick={() => {
              setParams(new URLSearchParams(), { replace: true });
            }}
          >
            Сбросить
          </Button>
        </div>
      )}

      {/* Область содержимого РАСТЯГИВАЕТСЯ: и заглушка, и числа живут в
          окне одного размера, иначе оно прыгает при каждом фильтре. */}
      <div data-analytics="body" className="min-h-0 flex-1">
        {data.data === undefined ? (
          <p className="text-foreground/80 text-sm">Считаем…</p>
        ) : (
          <Tab id={current.id} data={data.data} />
        )}
      </div>
    </div>
  );
}

function Tab({ id, data }: { id: string; data: AnalyticsPayload }) {
  if (data.totals.runs === 0) {
    /* Заглушка У КАЖДОЙ ВКЛАДКИ СВОЯ и говорит, что именно произойдёт:
       «нет данных» одинаковым текстом на четырёх вкладках не отвечает ни на
       один вопрос. Про симулятор здесь не повторяем - это сказано один раз,
       под заголовком окна. */
    const text: Record<string, { title: string; hint: string }> = {
      overview: {
        title: 'За этот период эфиров не было',
        hint: 'Первые числа появятся после первого эфира в TikTok.',
      },
      games: {
        title: 'Ни одна игра в эфир не выходила',
        hint: 'Игры появятся здесь сами, как только эфир состоится.',
      },
      audience: {
        title: 'Зрителей в этот период не было',
        hint: 'Комментарии, подарки и лайки считаются по ходу эфира.',
      },
      runs: {
        title: 'Эфиров в этот период нет',
        hint: 'Попробуйте другой период или снимите фильтры.',
      },
    };

    const empty = text[id] ?? text['overview'];

    return (
      <div
        data-analytics="empty"
        data-analytics-empty={id}
        /* По центру свободной площади и с ограниченной мерой строки: текст
           состояния, прижатый к левому краю во всю ширину, читается как
           ещё одна сноска к фильтрам. */
        className="flex h-full min-h-40 flex-col items-center justify-center gap-1 text-center"
      >
        <p className="text-foreground text-base font-semibold">{empty?.title}</p>
        <p className="text-foreground/80 max-w-md text-sm">{empty?.hint}</p>
      </div>
    );
  }

  if (id === 'games') {
    return (
      <ul data-analytics="games" className="flex flex-col gap-2">
        {data.games.map((game) => (
          <li
            key={game.gameId}
            data-analytics-game={game.gameId}
            className="border-foreground/10 rounded-lg flex items-center gap-3 border p-3 text-sm"
          >
            <span className="min-w-0 flex-1 truncate font-semibold">
              {data.filters.games.find((item) => item.id === game.gameId)?.title ?? game.gameId}
            </span>
            <span className="tabular-nums">{runsWord(game.runs)}</span>
            <span className="text-foreground/90 tabular-nums">{minutesWord(game.minutes)}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (id === 'runs') {
    return (
      <ul data-analytics="days" className="flex flex-col gap-2">
        {data.days.map((day) => (
          <li
            key={day.date}
            data-analytics-day={day.date}
            className="border-foreground/10 rounded-lg flex items-center gap-3 border p-3 text-sm"
          >
            <span className="min-w-0 flex-1 tabular-nums">
              {new Date(`${day.date}T00:00:00Z`).toLocaleDateString('ru-RU')}
            </span>
            <span className="tabular-nums">{runsWord(day.runs)}</span>
            <span className="text-foreground/90 tabular-nums">{minutesWord(day.minutes)}</span>
          </li>
        ))}
      </ul>
    );
  }

  const cells =
    id === 'audience'
      ? [
          { key: 'viewers', title: 'Зрителей за эфиры', value: data.totals.viewers },
          { key: 'comments', title: 'Комментариев', value: data.totals.comments },
          { key: 'likes', title: 'Лайков', value: data.totals.likes },
          { key: 'follows', title: 'Подписок', value: data.totals.follows },
          { key: 'shares', title: 'Репостов', value: data.totals.shares },
        ]
      : [
          { key: 'runs', title: 'Эфиров', value: data.totals.runs },
          { key: 'minutes', title: 'Минут в эфире', value: data.totals.minutes },
          { key: 'comments', title: 'Комментариев', value: data.totals.comments },
          { key: 'gifts', title: 'Подарков', value: data.totals.gifts },
          { key: 'giftCoins', title: 'Монет в подарках', value: data.totals.giftCoins },
        ];

  return (
    <ul data-analytics="totals" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {cells.map((cell) => (
        <li
          key={cell.key}
          data-analytics-cell={cell.key}
          className="border-foreground/10 rounded-lg border p-3"
        >
          <span className="text-foreground/80 block text-xs tracking-wide uppercase">
            {cell.title}
          </span>
          <span className="text-foreground block text-xl font-semibold tabular-nums">
            {cell.value}
          </span>
        </li>
      ))}
    </ul>
  );
}
